import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  setDoc,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useToast } from "../../contexts/ToastContext";
import "./TournamentManagement.css";

const TournamentManagement = () => {
  const { showSuccess, showError, showConfirm } = useToast();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, pending, accepted, rejected
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("registrationDate"); // registrationDate, teamName, status
  const [sortOrder, setSortOrder] = useState("desc"); // asc, desc
  const [tournamentVisible, setTournamentVisible] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [tournamentSettings, setTournamentSettings] = useState({
    tournamentName: "مسابقة ايجنايت للبولينغ",
    date: "13/9/2025",
    dayOfWeek: "يوم السبت",
    time: "6:00 مساءً",
    feePerPlayer: 60,
    playersPerTeam: 2,
    welcomeText: "متحمسين نشوفكم وتكونوا جزء من هالتحدي! فرصة حلوة تقضوا وقت ممتع وتتنافسوا على جوائز رهيبة!",
    registrationRules: [
      "يتثبت التسجيل فور وصول رسوم الاشتراك",
      "التسجيل متاح حتى اكتمال العدد المطلوب",
    ],
    tournamentRules: [
      "الفريق صاحب أعلى نتيجة في كل جولة يتأهل إلى النهائي",
      "كل فريق سيشارك في جولة واحدة فقط ما عدا الجولة النهائية في حال التأهل",
      "يتنافس أفضل فرق في الجولة النهائية لتحديد الفائز",
      "يتم لعب جولة اضافية في حال التعادل",
      "يجب على اللاعبين انتظار دورهم واحترام حدود المسارات",
      "يجب قراءة والالتزام بالارشادات المتواجدة في منطقة البولينغ",
      "أي تصرف غير اخلاقي يؤدي إلى استبعاد اللاعب",
    ],
  });

  // Load tournament settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, "settings", "tournament"));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setTournamentVisible(data.visible || false);
          if (data.tournamentName) setTournamentSettings(prev => ({ ...prev, tournamentName: data.tournamentName }));
          if (data.date) setTournamentSettings(prev => ({ ...prev, date: data.date }));
          if (data.dayOfWeek) setTournamentSettings(prev => ({ ...prev, dayOfWeek: data.dayOfWeek }));
          if (data.time) setTournamentSettings(prev => ({ ...prev, time: data.time }));
          if (data.feePerPlayer) setTournamentSettings(prev => ({ ...prev, feePerPlayer: data.feePerPlayer }));
          if (data.playersPerTeam) setTournamentSettings(prev => ({ ...prev, playersPerTeam: data.playersPerTeam }));
          if (data.welcomeText) setTournamentSettings(prev => ({ ...prev, welcomeText: data.welcomeText }));
          if (data.registrationRules) setTournamentSettings(prev => ({ ...prev, registrationRules: data.registrationRules }));
          if (data.tournamentRules) setTournamentSettings(prev => ({ ...prev, tournamentRules: data.tournamentRules }));
        }
      } catch (error) {
        console.error("Error loading tournament settings:", error);
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();
  }, []);

  useEffect(() => {
    const q = query(
      collection(db, "tournamentRegistrations"),
      orderBy("registrationDate", "desc")
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const registrationsData = [];
      querySnapshot.forEach((doc) => {
        registrationsData.push({
          id: doc.id,
          ...doc.data(),
          registrationDate:
            doc.data().registrationDate?.toDate?.() || new Date(),
        });
      });
      setRegistrations(registrationsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSettingsChange = (field, value) => {
    setTournamentSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleRuleChange = (type, index, value) => {
    setTournamentSettings(prev => {
      const rules = [...prev[type]];
      rules[index] = value;
      return { ...prev, [type]: rules };
    });
  };

  const handleAddRule = (type) => {
    setTournamentSettings(prev => ({
      ...prev,
      [type]: [...prev[type], ""],
    }));
  };

  const handleRemoveRule = (type, index) => {
    setTournamentSettings(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }));
  };

  const handleSaveSettings = async () => {
    try {
      await setDoc(
        doc(db, "settings", "tournament"),
        {
          ...tournamentSettings,
          visible: tournamentVisible,
          updatedAt: new Date(),
        },
        { merge: true }
      );
      setShowSettingsModal(false);
      showSuccess("Tournament settings saved successfully");
    } catch (error) {
      console.error("Error saving tournament settings:", error);
      showError("Failed to save tournament settings");
    }
  };

  const handleToggleVisibility = async () => {
    const newVisibility = !tournamentVisible;
    const confirmed = await showConfirm({
      title: `${newVisibility ? "Show" : "Hide"} Tournament Registration`,
      message: `Are you sure you want to ${
        newVisibility ? "show" : "hide"
      } the tournament registration button on the home page?`,
      confirmText: newVisibility ? "Show" : "Hide",
      cancelText: "Cancel",
      type: "info",
    });

    if (confirmed) {
      try {
        await setDoc(
          doc(db, "settings", "tournament"),
          {
            visible: newVisibility,
            updatedAt: new Date(),
          },
          { merge: true }
        );
        setTournamentVisible(newVisibility);
        showSuccess(
          `Tournament registration ${
            newVisibility ? "shown" : "hidden"
          } successfully`
        );
      } catch (error) {
        console.error("Error updating tournament visibility:", error);
        showError("Failed to update tournament visibility");
      }
    }
  };

  const handleResetTournament = async () => {
    const confirmed = await showConfirm({
      title: "Reset Tournament",
      message: `Are you sure you want to delete ALL ${registrations.length} tournament registrations? This action cannot be undone.`,
      confirmText: "Delete All",
      cancelText: "Cancel",
      type: "danger",
    });

    if (confirmed) {
      try {
        const querySnapshot = await getDocs(
          collection(db, "tournamentRegistrations")
        );
        const batch = writeBatch(db);

        querySnapshot.forEach((document) => {
          batch.delete(document.ref);
        });

        await batch.commit();
        showSuccess(
          `Successfully deleted ${querySnapshot.size} tournament registrations`
        );
      } catch (error) {
        console.error("Error resetting tournament:", error);
        showError("Failed to reset tournament");
      }
    }
  };

  const handleStatusUpdate = async (registrationId, newStatus) => {
    const statusText = newStatus === "accepted" ? "accept" : "reject";
    const confirmed = await showConfirm({
      title: `${newStatus === "accepted" ? "Accept" : "Reject"} Registration`,
      message: `Are you sure you want to ${statusText} this team's registration?`,
      confirmText: newStatus === "accepted" ? "Accept" : "Reject",
      cancelText: "Cancel",
      type: newStatus === "accepted" ? "info" : "warning",
    });

    if (confirmed) {
      try {
        await updateDoc(doc(db, "tournamentRegistrations", registrationId), {
          status: newStatus,
          updatedAt: new Date(),
        });
        showSuccess(`Registration ${newStatus} successfully`);
      } catch (error) {
        console.error("Error updating status:", error);
        showError("Failed to update registration status");
      }
    }
  };

  const handleDelete = async (registrationId) => {
    const confirmed = await showConfirm({
      title: "Delete Registration",
      message:
        "Are you sure you want to delete this registration? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger",
    });

    if (confirmed) {
      try {
        await deleteDoc(doc(db, "tournamentRegistrations", registrationId));
        showSuccess("Registration deleted successfully");
      } catch (error) {
        console.error("Error deleting registration:", error);
        showError("Failed to delete registration");
      }
    }
  };

  const filteredRegistrations = registrations.filter((registration) => {
    const matchesFilter = filter === "all" || registration.status === filter;
    const matchesSearch =
      registration.teamName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      registration.player1Name
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      registration.player2Name
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      registration.contactNumber.includes(searchTerm);

    return matchesFilter && matchesSearch;
  });

  // Sort registrations
  const sortedRegistrations = [...filteredRegistrations].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case "teamName":
        aValue = a.teamName.toLowerCase();
        bValue = b.teamName.toLowerCase();
        break;
      case "status":
        aValue = a.status;
        bValue = b.status;
        break;
      case "registrationDate":
      default:
        aValue = a.registrationDate;
        bValue = b.registrationDate;
        break;
    }

    if (sortOrder === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "pending":
        return "status-pending";
      case "accepted":
        return "status-accepted";
      case "rejected":
        return "status-rejected";
      default:
        return "status-pending";
    }
  };

  const getStatusBadgeText = (status) => {
    switch (status) {
      case "pending":
        return "Pending";
      case "accepted":
        return "Accepted";
      case "rejected":
        return "Rejected";
      default:
        return "Pending";
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  if (loading) {
    return (
      <div className="tournament-management">
        <div className="tournament-container">
          <div className="loading">Loading tournament registrations...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="tournament-management">
      <div className="tournament-container">
        <div className="tournament-header">
          <h1>🏆 Tournament Management</h1>
          <p>Manage tournament registrations and participants</p>
        </div>

        <div className="tournament-admin-controls">
          <div className="admin-control-card">
            <h3>🎯 Tournament Settings</h3>
            <div className="control-group">
              <div className="control-item">
                <label>
                  <span className="control-label">
                    Show Registration on Home Page:
                  </span>
                  <span
                    className={`status-indicator ${
                      tournamentVisible ? "active" : "inactive"
                    }`}
                  >
                    {tournamentVisible ? "Visible" : "Hidden"}
                  </span>
                </label>
                <button
                  className={`toggle-btn ${
                    tournamentVisible ? "hide-btn" : "show-btn"
                  }`}
                  onClick={handleToggleVisibility}
                  disabled={settingsLoading}
                >
                  {tournamentVisible
                    ? "🙈 Hide Registration"
                    : "👁️ Show Registration"}
                </button>
              </div>
              <div className="control-item">
                <label>
                  <span className="control-label">Edit Tournament Details:</span>
                  <span className="control-description">
                    Update date, price, terms, and other settings
                  </span>
                </label>
                <button
                  className="toggle-btn show-btn"
                  onClick={() => setShowSettingsModal(true)}
                  disabled={settingsLoading}
                >
                  ⚙️ Edit Settings
                </button>
              </div>
              <div className="control-item">
                <label>
                  <span className="control-label">Reset Tournament:</span>
                  <span className="control-description">
                    Delete all registrations ({registrations.length} total)
                  </span>
                </label>
                <button
                  className="reset-btn danger-btn"
                  onClick={handleResetTournament}
                  disabled={loading || registrations.length === 0}
                >
                  🗑️ Reset Tournament
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Settings Modal */}
        {showSettingsModal && (
          <div className="settings-modal-overlay" onClick={() => setShowSettingsModal(false)}>
            <div className="settings-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="settings-modal-header">
                <h2>⚙️ Edit Tournament Settings</h2>
                <button
                  className="tr-modal-close-btn"
                  onClick={() => setShowSettingsModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="settings-modal-body">
                <div className="settings-form-group">
                  <label>Tournament Name (Arabic):</label>
                  <input
                    type="text"
                    value={tournamentSettings.tournamentName}
                    onChange={(e) => handleSettingsChange("tournamentName", e.target.value)}
                    placeholder="e.g., مسابقة ايجنايت للبولينغ"
                  />
                </div>
                <div className="settings-form-group">
                  <label>Tournament Date:</label>
                  <input
                    type="text"
                    value={tournamentSettings.date}
                    onChange={(e) => handleSettingsChange("date", e.target.value)}
                    placeholder="e.g., 13/9/2025"
                  />
                </div>
                <div className="settings-form-group">
                  <label>Day of Week (Arabic):</label>
                  <input
                    type="text"
                    value={tournamentSettings.dayOfWeek}
                    onChange={(e) => handleSettingsChange("dayOfWeek", e.target.value)}
                    placeholder="e.g., يوم السبت"
                  />
                </div>
                <div className="settings-form-group">
                  <label>Time (Arabic):</label>
                  <input
                    type="text"
                    value={tournamentSettings.time}
                    onChange={(e) => handleSettingsChange("time", e.target.value)}
                    placeholder="e.g., 6:00 مساءً"
                  />
                </div>
                <div className="settings-form-group">
                  <label>Fee Per Player (Shekel):</label>
                  <input
                    type="number"
                    value={tournamentSettings.feePerPlayer}
                    onChange={(e) => handleSettingsChange("feePerPlayer", parseInt(e.target.value) || 0)}
                    placeholder="60"
                  />
                </div>
                <div className="settings-form-group">
                  <label>Players Per Team:</label>
                  <input
                    type="number"
                    value={tournamentSettings.playersPerTeam}
                    onChange={(e) => handleSettingsChange("playersPerTeam", parseInt(e.target.value) || 2)}
                    placeholder="2"
                  />
                </div>
                <div className="settings-form-group">
                  <label>Welcome Text (Arabic):</label>
                  <textarea
                    value={tournamentSettings.welcomeText}
                    onChange={(e) => handleSettingsChange("welcomeText", e.target.value)}
                    placeholder="Welcome message..."
                    rows="3"
                  />
                </div>

                <div className="settings-form-group">
                  <label>Registration Rules (شروط التسجيل):</label>
                  <div className="rules-editor">
                    {tournamentSettings.registrationRules.map((rule, index) => (
                      <div key={index} className="rule-item">
                        <input
                          type="text"
                          value={rule}
                          onChange={(e) => handleRuleChange("registrationRules", index, e.target.value)}
                          placeholder={`Rule ${index + 1}`}
                        />
                        <button
                          type="button"
                          className="remove-rule-btn"
                          onClick={() => handleRemoveRule("registrationRules", index)}
                          disabled={tournamentSettings.registrationRules.length === 1}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="add-rule-btn"
                      onClick={() => handleAddRule("registrationRules")}
                    >
                      + Add Registration Rule
                    </button>
                  </div>
                </div>

                <div className="settings-form-group">
                  <label>Tournament Rules (قواعد المسابقة):</label>
                  <div className="rules-editor">
                    {tournamentSettings.tournamentRules.map((rule, index) => (
                      <div key={index} className="rule-item">
                        <input
                          type="text"
                          value={rule}
                          onChange={(e) => handleRuleChange("tournamentRules", index, e.target.value)}
                          placeholder={`Rule ${index + 1}`}
                        />
                        <button
                          type="button"
                          className="remove-rule-btn"
                          onClick={() => handleRemoveRule("tournamentRules", index)}
                          disabled={tournamentSettings.tournamentRules.length === 1}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="add-rule-btn"
                      onClick={() => handleAddRule("tournamentRules")}
                    >
                      + Add Tournament Rule
                    </button>
                  </div>
                </div>
              </div>
              <div className="settings-modal-footer">
                <button
                  className="modal-cancel-btn"
                  onClick={() => setShowSettingsModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="modal-save-btn"
                  onClick={handleSaveSettings}
                >
                  💾 Save Settings
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="tournament-controls">
          <div className="search-filter-section">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search by team name, player names, or phone number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="filter-sort-section">
              <div className="filter-buttons">
                <button
                  className={`filter-btn ${filter === "all" ? "active" : ""}`}
                  onClick={() => setFilter("all")}
                >
                  All ({registrations.length})
                </button>
                <button
                  className={`filter-btn ${
                    filter === "pending" ? "active" : ""
                  }`}
                  onClick={() => setFilter("pending")}
                >
                  Pending (
                  {registrations.filter((r) => r.status === "pending").length})
                </button>
                <button
                  className={`filter-btn ${
                    filter === "accepted" ? "active" : ""
                  }`}
                  onClick={() => setFilter("accepted")}
                >
                  Accepted (
                  {registrations.filter((r) => r.status === "accepted").length})
                </button>
                <button
                  className={`filter-btn ${
                    filter === "rejected" ? "active" : ""
                  }`}
                  onClick={() => setFilter("rejected")}
                >
                  Rejected (
                  {registrations.filter((r) => r.status === "rejected").length})
                </button>
              </div>

              <div className="sort-controls">
                <label htmlFor="sortBy">Sort by:</label>
                <select
                  id="sortBy"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="sort-select"
                >
                  <option value="registrationDate">Registration Date</option>
                  <option value="teamName">Team Name</option>
                  <option value="status">Status</option>
                </select>

                <button
                  className={`sort-btn ${sortOrder === "asc" ? "active" : ""}`}
                  onClick={() => setSortOrder("asc")}
                  title="Ascending"
                >
                  ↑
                </button>
                <button
                  className={`sort-btn ${sortOrder === "desc" ? "active" : ""}`}
                  onClick={() => setSortOrder("desc")}
                  title="Descending"
                >
                  ↓
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="tournament-stats">
          <div className="stat-card">
            <h3>Total Registrations</h3>
            <p className="stat-number">{registrations.length}</p>
          </div>
          <div className="stat-card">
            <h3>Pending Review</h3>
            <p className="stat-number pending">
              {registrations.filter((r) => r.status === "pending").length}
            </p>
          </div>
          <div className="stat-card">
            <h3>Accepted</h3>
            <p className="stat-number accepted">
              {registrations.filter((r) => r.status === "accepted").length}
            </p>
          </div>
          <div className="stat-card">
            <h3>Rejected</h3>
            <p className="stat-number rejected">
              {registrations.filter((r) => r.status === "rejected").length}
            </p>
          </div>
        </div>

        <div className="registrations-list">
          {filteredRegistrations.length === 0 ? (
            <div className="no-registrations">
              <p>No registrations found matching your criteria.</p>
            </div>
          ) : (
            sortedRegistrations.map((registration) => (
              <div key={registration.id} className="registration-card">
                <div className="registration-header">
                  <div className="team-info">
                    <h3>{registration.teamName}</h3>
                    <span
                      className={`status-badge ${getStatusBadgeClass(
                        registration.status
                      )}`}
                    >
                      {getStatusBadgeText(registration.status)}
                    </span>
                  </div>
                  <div className="registration-date">
                    Registered: {formatDate(registration.registrationDate)}
                  </div>
                </div>

                <div className="players-info">
                  <div className="player">
                    <strong>Player 1:</strong> {registration.player1Name} (Age:{" "}
                    {registration.player1Age})
                  </div>
                  <div className="player">
                    <strong>Player 2:</strong> {registration.player2Name} (Age:{" "}
                    {registration.player2Age})
                  </div>
                </div>

                <div className="contact-info">
                  <div className="contact-item">
                    <strong>Contact:</strong> {registration.contactNumber}
                  </div>
                  <div className="contact-item">
                    <strong>How they heard:</strong>{" "}
                    {registration.howDidYouHear}
                  </div>
                  <div className="contact-item">
                    <strong>Total Fee:</strong> {registration.totalFee} شيكل
                  </div>
                </div>

                <div className="registration-actions">
                  {registration.status === "pending" && (
                    <>
                      <button
                        className="action-btn accept-btn"
                        onClick={() =>
                          handleStatusUpdate(registration.id, "accepted")
                        }
                      >
                        ✓ Accept
                      </button>
                      <button
                        className="action-btn reject-btn"
                        onClick={() =>
                          handleStatusUpdate(registration.id, "rejected")
                        }
                      >
                        ✗ Reject
                      </button>
                    </>
                  )}

                  {registration.status === "accepted" && (
                    <button
                      className="action-btn reject-btn"
                      onClick={() =>
                        handleStatusUpdate(registration.id, "rejected")
                      }
                    >
                      ✗ Reject
                    </button>
                  )}

                  {registration.status === "rejected" && (
                    <button
                      className="action-btn accept-btn"
                      onClick={() =>
                        handleStatusUpdate(registration.id, "accepted")
                      }
                    >
                      ✓ Accept
                    </button>
                  )}

                  <button
                    className="action-btn tournament-delete-btn"
                    onClick={() => handleDelete(registration.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default TournamentManagement;
